import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { post } from '../../services/api/apiClient.js';
import Modal from '../../components/common/Modal.jsx';
import { today } from '../finanzas-cxp/shared.js';

/**
 * Modal de captura de Gastos / Cargos de un contenedor. Réplica de los modales
 * "Gastos Contenedor X" / "Cargos Contenedor X" de V1 (`ListadoContenedoresAdmin.jsx` y
 * `BuscarContenedor.jsx`): Fecha, Cliente (deshabilitado), Moneda, Tipo de Cambio (solo USD)
 * y renglones múltiples (Proveedor [solo gastos] / Concepto / Descripcion / Importe) con
 * botones -/+; al guardar crea UN registro por renglón (igual que V1).
 */
function renglonNuevo() {
  return { id: crypto.randomUUID(), proveedor: '', concepto: '', descripcion: 'NA', importe: 0 };
}

export default function CapturaGastosCargosModal({
  open, onClose, tipo, contenedor, proveedores, conceptos, clientes, onGuardado,
}) {
  const esGasto = tipo === 'gastos';
  const [fecha, setFecha] = useState(today());
  const [moneda, setMoneda] = useState('MXN');
  const [tipoCambio, setTipoCambio] = useState(1);
  const [renglones, setRenglones] = useState([renglonNuevo()]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) {
      setFecha(today()); setMoneda('MXN'); setTipoCambio(1); setRenglones([renglonNuevo()]); setGuardando(false);
    }
  }, [open]);

  function setR(id, campo, valor) {
    setRenglones((rs) => rs.map((r) => (r.id === id ? { ...r, [campo]: valor } : r)));
  }

  async function guardar() {
    if (renglones.some((r) => !r.concepto || (esGasto && !r.proveedor))) {
      Swal.fire({ icon: 'error', title: 'Faltan datos', text: esGasto ? 'Proveedor y concepto son obligatorios' : 'El concepto es obligatorio' });
      return;
    }
    setGuardando(true);
    try {
      const tc = Number(tipoCambio) || 1;
      for (const r of renglones) {
        const importe = Number(r.importe) || 0;
        const total = importe * (moneda === 'USD' ? tc : 1);
        const body = {
          fecha,
          totalUSD: importe,
          tipoCambio: moneda === 'USD' ? tc : 1,
          total,
          abonos: 0,
          saldo: total,
          descripcion: r.descripcion,
          conceptosGastos: r.concepto,
          contenedores: contenedor._id,
          clientes: contenedor.idClientes,
          moneda,
        };
        if (esGasto) body.proveedores = r.proveedor;
        // eslint-disable-next-line no-await-in-loop
        await post(esGasto ? '/gastos-contenedor' : '/cargos-contenedor', body);
      }
      await Swal.fire('Good job!', 'Creado con exito', 'success');
      onClose();
      onGuardado?.();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: e.message || 'Something went wrong!' });
    } finally {
      setGuardando(false);
    }
  }

  const titulo = `${esGasto ? 'Gastos' : 'Cargos'} Contenedor ${contenedor?.numeroInterno || ''}`;
  const cols = esGasto ? 'grid-cols-[1fr_1fr_1fr_1fr_auto]' : 'grid-cols-[1fr_1fr_1fr_auto]';

  return (
    <Modal open={open} onClose={onClose} title={titulo} width={esGasto ? 'max-w-5xl' : 'max-w-3xl'}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Fecha</label>
            <input type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)} className="input-base" />
          </div>
          <div>
            <label className="label">Cliente</label>
            <select disabled value={contenedor?.idClientes || '0'} className="select-base">
              <option value="0">Selecciona</option>
              {[...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((c) => (
                <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Moneda</label>
            <select value={moneda} onChange={(e) => { setMoneda(e.target.value); setTipoCambio(1); }} className="select-base">
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </select>
          </div>
          {moneda === 'USD' && (
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" min="0" step="any" required value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} className="input-base" />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className={`grid gap-2 ${cols}`}>
            {esGasto && <label className="label">Proveedor</label>}
            <label className="label">Concepto</label>
            <label className="label">Descripcion</label>
            <label className="label">Importe</label>
            <span />
          </div>
          {renglones.map((r) => (
            <div key={r.id} className={`grid gap-2 items-center ${cols}`}>
              {esGasto && (
                <select value={r.proveedor} onChange={(e) => setR(r.id, 'proveedor', e.target.value)} className="select-base">
                  <option value="">Selecciona</option>
                  {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              )}
              <select value={r.concepto} onChange={(e) => setR(r.id, 'concepto', e.target.value)} className="select-base">
                <option value="">Selecciona</option>
                {[...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1)).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <input type="text" placeholder="descripcion" value={r.descripcion} onChange={(e) => setR(r.id, 'descripcion', e.target.value)} className="input-base" />
              <input type="number" min="0" step="any" value={r.importe} onChange={(e) => setR(r.id, 'importe', e.target.value)} className="input-base" />
              <div className="flex gap-1">
                <button type="button" tabIndex={-1} disabled={renglones.length === 1} className="btn-outline px-2 py-1 text-xs disabled:opacity-40"
                  onClick={() => setRenglones((rs) => rs.filter((x) => x.id !== r.id))}>-</button>
                <button type="button" tabIndex={-1} className="btn-outline px-2 py-1 text-xs"
                  onClick={() => setRenglones((rs) => [...rs, renglonNuevo()])}>+</button>
              </div>
            </div>
          ))}
        </div>

        <button type="button" onClick={guardar} disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
}
